/**
 * Shared Zustand store for Diary events + Availability statuses.
 *
 * Both MyDiaryPage and AvailabilityPage read/write here so they stay in sync:
 *  – Adding an event on a date marks that date as "busy" in availability.
 *  – Setting a date to "leave" in availability is visible in the diary.
 *  – Deleting all events from a date clears the auto-busy flag.
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import * as calendarApi from './calendarApi';
import * as hearingsApi from './hearingsApi';

/* ─── Types ─── */

export type EventTone = 'brand' | 'amber' | 'red' | 'emerald' | 'violet';
// hearing/conference/task/brief_return are DOMAIN objects — they are only ever DISPLAYED in
// the diary (projected from their source), never created there. meeting/travel/leave/personal
// are true calendar-native objects the diary genuinely owns and may create.
export type EventKind = 'hearing' | 'conference' | 'task' | 'brief_return' | 'personal' | 'meeting' | 'travel' | 'leave';

/** The ONLY kinds the diary may CREATE — true calendar objects. Domain objects (hearing,
 *  conference, task, …) are born in their own workspaces and merely appear here. */
export const CREATABLE_KINDS: EventKind[] = ['personal', 'meeting', 'travel', 'leave'];
export type AvailStatus = 'available' | 'partial' | 'busy' | 'leave';

export interface CalEvent {
  id: string;
  /** ISO date string yyyy-MM-dd */
  date: string;
  /** Start hour 0-8 (maps to 09:00–17:00) — used for calendar-grid positioning */
  start: number;
  /** Exact start time "HH:MM" — preferred over the slot index when present */
  startTime?: string;
  /** Duration in hours */
  span: number;
  title: string;
  sub?: string;
  location?: string;
  kind: EventKind;
  tone: EventTone;
  caseId?: string;
  hearingId?: string;
  notes?: string;
  /** Clerk userIds the barrister asked to coordinate this event (send-only; not read back). */
  coordinationClerkIds?: string[];
  /** User UUID of the event owner — use for identity checks, not the name string */
  ownerId?: string;
  /** Which barrister this event belongs to (display name e.g. "James Thornton KC") */
  barrister?: string;
  /** Who created this event */
  createdBy?: string;
  /** Lifecycle status — drives the badge shown on the event */
  status?: 'scheduled' | 'confirmed' | 'tentative' | 'completed' | 'cancelled';
  /** Who the meeting is with */
  attendees?: string;
  /** Matter context composed server-side (RC-DIARY Stage 1) — consumed, never recomputed */
  matter?: {
    number?: string;
    name?: string;
    status?: string;
    priority?: string;
    client?: string;
    court?: string;
    opponent?: string;
  };
  /** Hearing context when this event is a hearing */
  hearingType?: string;
  hearingStatus?: string;
  judge?: string;
  courtroom?: string;
  /** RC-STAGE3 card enrichment (from /calendar batch readers) — consumed, never recomputed. */
  readinessPct?: number;
  obligationsBlocked?: number;
  lifecycle?: string;
}

export const KIND_LABEL: Record<EventKind, string> = {
  hearing: 'Hearing', conference: 'Conference', task: 'Task', brief_return: 'Brief return', personal: 'Personal',
  meeting: 'Meeting', travel: 'Travel', leave: 'Away',
};

export const KIND_TONE: Record<EventKind, EventTone> = {
  hearing: 'brand', conference: 'amber', task: 'violet', brief_return: 'red', personal: 'emerald',
  meeting: 'violet', travel: 'emerald', leave: 'red',
};

export const AVAIL_STYLE: Record<AvailStatus, string> = {
  available: 'bg-emerald-100 dark:bg-emerald-500/15 text-emerald-800 dark:text-emerald-300 hover:bg-emerald-200 dark:hover:bg-emerald-500/25',
  partial:   'bg-amber-100 dark:bg-amber-500/15 text-amber-800 dark:text-amber-300 hover:bg-amber-200 dark:hover:bg-amber-500/25',
  busy:      'bg-brand-100 dark:bg-brand-900/40 text-brand-800 dark:text-brand-300 hover:bg-brand-200 dark:hover:bg-brand-900/50',
  leave:     'bg-slate-200 dark:bg-slate-700/40 text-slate-700 dark:text-slate-300 hover:bg-slate-300 dark:hover:bg-slate-700/60',
};

export const AVAIL_LABEL: Record<AvailStatus, string> = {
  available: 'Available', partial: 'Partial', busy: 'Booked', leave: 'On leave',
};

export const TONE_STYLE: Record<EventTone, string> = {
  brand:   'bg-brand-50 dark:bg-brand-900/30 border-brand-200 dark:border-brand-700/50 text-brand-900 dark:text-brand-200',
  amber:   'bg-amber-50 dark:bg-amber-500/10 border-amber-200 dark:border-amber-700/50 text-amber-900 dark:text-amber-200',
  red:     'bg-red-50 dark:bg-red-500/10 border-red-200 dark:border-red-700/50 text-red-900 dark:text-red-200',
  emerald: 'bg-emerald-50 dark:bg-emerald-500/10 border-emerald-200 dark:border-emerald-700/50 text-emerald-900 dark:text-emerald-200',
  violet:  'bg-violet-50 dark:bg-violet-500/10 border-violet-200 dark:border-violet-700/50 text-violet-900 dark:text-violet-200',
};

export const HOURS = ['09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00'];

export type EventStatus = NonNullable<CalEvent['status']>;
export const EVENT_STATUSES: EventStatus[] = ['scheduled', 'confirmed', 'tentative', 'completed', 'cancelled'];
export const EVENT_STATUS_LABEL: Record<EventStatus, string> = {
  scheduled: 'Scheduled', confirmed: 'Confirmed', tentative: 'Pending', completed: 'Completed', cancelled: 'Cancelled',
};
export const EVENT_STATUS_BADGE: Record<EventStatus, string> = {
  scheduled: 'bg-blue-100 text-blue-800 dark:text-blue-200',
  confirmed: 'bg-emerald-200 text-emerald-800 dark:text-emerald-200',
  tentative: 'bg-amber-200 text-amber-800 dark:text-amber-200',
  completed: 'bg-slate-200 text-slate-700 dark:text-slate-300',
  cancelled: 'bg-red-100 text-red-700 dark:text-red-300',
};

/* ─── Helpers ─── */

/** yyyy-MM-dd for a locally-constructed calendar-day Date (grid cells, pickers). */
export function dateKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * TODAY's calendar date in the PRACTICE's timezone, "yyyy-MM-dd".
 *
 * DELIBERATELY NOT the viewer's timezone. Calendar events are keyed to the practice's wall
 * date, so "today" has to be computed against the same clock — otherwise a viewer in another
 * timezone sees tomorrow near midnight and every "today" filter is off by a day (TZ-1).
 *
 * This is the boundary `appearance.timezone` must not cross: a clerk's personal display
 * setting changes how a time is WRITTEN, never which practice day an event belongs to. Callers
 * pass the chambers timezone (`useAuthStore.chambers.timezone`, or `usePracticeTimezone()`);
 * the London default applies only before the session is known.
 */
export function todayKey(tz = 'Europe/London'): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

let _nextId = 100;
export function nextEventId(): string {
  return `ev-${_nextId++}-${Date.now()}`;
}

/* ─── API helpers ─── */

function kindToEventType(kind: EventKind): string {
  const map: Record<EventKind, string> = {
    hearing: 'hearing', conference: 'conference',
    task: 'other', brief_return: 'other', personal: 'personal',
    meeting: 'meeting', travel: 'travel', leave: 'out_of_office',
  };
  return map[kind] ?? 'other';
}

// Diary times are timezone-stable wall-clock — written with a trailing "Z" and read back
// in UTC (see calendarApi.apiToCalEvent). Without the "Z" the server would parse the naive
// string in its own zone, and the browser would re-read it in the local zone, shifting the
// slot the event lands on (and colliding with hearing events, which already write "Z").
function buildStartAt(e: CalEvent): string {
  const t = e.startTime ?? HOURS[e.start] ?? '09:00';
  return `${e.date}T${t}:00Z`;
}

function buildEndAt(e: CalEvent): string {
  // Exact time takes precedence — end = start + span hours, keeping the minutes.
  if (e.startTime) {
    const [h, m] = e.startTime.split(':').map(Number);
    const endH = Math.min(23, (h ?? 9) + e.span);
    return `${e.date}T${String(endH).padStart(2, '0')}:${String(m ?? 0).padStart(2, '0')}:00Z`;
  }
  const endSlot = e.start + e.span;
  return `${e.date}T${HOURS[Math.min(endSlot, HOURS.length - 1)] ?? '17:00'}:00Z`;
}

/** Display label for an event's start — exact time when set, else the slot hour. */
export function eventStartLabel(e: { start: number; startTime?: string }): string {
  return e.startTime ?? HOURS[e.start] ?? '09:00';
}

/** Display label for an event's end — start + span hours, exact minutes when set. */
export function eventEndLabel(e: { start: number; startTime?: string; span: number }): string {
  if (e.startTime) {
    const [h, m] = e.startTime.split(':').map(Number);
    const endH = Math.min(23, (h ?? 9) + e.span);
    return `${String(endH).padStart(2, '0')}:${String(m ?? 0).padStart(2, '0')}`;
  }
  return HOURS[Math.min(e.start + e.span, HOURS.length - 1)] ?? '17:00';
}

/** Convert "HH:MM" to the nearest 09:00–17:00 slot index for grid positioning. */
export function timeToSlotIndex(hhmm: string): number {
  const h = parseInt(hhmm.split(':')[0] ?? '9', 10);
  return Math.max(0, Math.min(HOURS.length - 1, h - 9));
}

/* ─── Store ─── */

interface DiaryState {
  events: CalEvent[];
  /** Manual availability overrides (paint mode). Key = yyyy-MM-dd */
  availability: Record<string, AvailStatus>;
  loading: boolean;
  /** Set when the last load failed — the diary must not read as "no events" for it. Not persisted. */
  error: string | null;

  /** Fetch events from the real API */
  fetchEvents: () => Promise<void>;

  // Event actions — POST/DELETE to real API, then fetchEvents()
  addEvent: (e: CalEvent) => Promise<void>;
  updateEvent: (id: string, patch: Partial<CalEvent>) => Promise<void>;
  deleteEvent: (id: string) => Promise<void>;

  // Availability actions
  setAvailability: (date: string, status: AvailStatus) => void;
  clearAvailability: (date: string) => void;

  /** Compute the effective status for a date (events + manual override) */
  getStatus: (date: string) => AvailStatus | null;

  /** Get events for a specific date */
  getEventsForDate: (date: string) => CalEvent[];

  /** Get events for a specific date grouped by barrister */
  getEventsForDateByBarrister: (date: string) => Record<string, CalEvent[]>;

  /** Get all events for a barrister on a date */
  getBarristerEvents: (barrister: string, date: string) => CalEvent[];
}

export const useDiaryStore = create<DiaryState>()(
  persist(
    (set, get) => ({
      events: [],
      availability: {},
      loading: false,
      error: null,

      fetchEvents: async () => {
        set({ loading: true });
        try {
          const apiEvents = await calendarApi.fetchCalendarEvents();
          // Prune availability overrides older than 90 days so localStorage doesn't grow without bound
          const cutoff = new Date(Date.now() - 90 * 86_400_000).toISOString().slice(0, 10);
          set((s) => ({
            events: apiEvents,
            loading: false,
            error: null,
            availability: Object.fromEntries(
              Object.entries(s.availability).filter(([k]) => k >= cutoff),
            ),
          }));
        } catch (err) {
          console.error('Failed to fetch calendar events:', err);
          set({ loading: false, error: 'Your diary could not be loaded' });
        }
      },

      addEvent: async (e) => {
        // Optimistic update immediately
        set((s) => ({ events: [...s.events, e] }));
        try {
          await calendarApi.createCalendarEvent({
            title: e.title,
            startAt: buildStartAt(e),
            endAt: buildEndAt(e),
            eventType: kindToEventType(e.kind),
            status: e.status,
            description: e.notes,
            location: e.location,
            caseId: e.caseId,
            // If clerk set ownerId to a barrister's userId, honour it
            // so the event appears in the barrister's diary, not the clerk's
            forUserId: e.ownerId ?? undefined,
            // Clerk(s) asked to coordinate — server validates + notifies them.
            coordinationClerkIds: e.coordinationClerkIds?.length ? e.coordinationClerkIds : undefined,
          });
          // Replace optimistic with real server data
          await get().fetchEvents();
        } catch (err) {
          console.error('Failed to create calendar event:', err);
          // Rollback optimistic
          set((s) => ({ events: s.events.filter((ev) => ev.id !== e.id) }));
          throw err;
        }
      },

      updateEvent: async (id, patch) => {
        const existing = get().events.find((ev) => ev.id === id);
        // Optimistic local update
        set((s) => ({
          events: s.events.map((ev) => (ev.id === id ? { ...ev, ...patch } : ev)),
        }));
        try {
          if (existing?.hearingId) {
            // This diary event is a PROJECTION of a hearing. Edit the HEARING (the canonical
            // source) — NOT just the calendar-event copy — so the change (date/time, status,
            // notes) propagates to the matter, the agenda and back into the diary. Editing the
            // event copy alone would silently never reach the matter (Raj: "if I update from the
            // diary it must reflect everywhere on that matter").
            const date = patch.date ?? existing.date;
            const time = patch.startTime ?? existing.startTime ?? '10:00';
            const statusMap: Record<string, string> = {
              scheduled: 'scheduled', confirmed: 'confirmed', tentative: 'scheduled',
              completed: 'completed', cancelled: 'vacated',
            };
            await hearingsApi.updateHearing(existing.hearingId, {
              // wall-clock-as-UTC convention (same as NewHearingPage / hearingsStore)
              hearingDate: `${date}T${time}:00Z`,
              ...(patch.status !== undefined ? { status: statusMap[patch.status] ?? 'scheduled' } : {}),
              ...(patch.notes !== undefined ? { notes: patch.notes } : {}),
            });
          } else {
            // Self-owned calendar event (personal / meeting / travel / consultation): the
            // CalendarEvent IS its own source of truth, so edit it directly — but INCLUDE
            // date/time, which was previously dropped (the modal appeared to save, the time
            // never changed). Times use the wall-clock-as-UTC convention, same as everywhere.
            const timeChanged = patch.date !== undefined || patch.startTime !== undefined || patch.span !== undefined;
            let timePatch: { startAt: string; endAt: string } | undefined;
            if (timeChanged && existing) {
              const date = patch.date ?? existing.date;
              const time = patch.startTime ?? existing.startTime ?? '10:00';
              const span = patch.span ?? existing.span ?? 1;
              const startAt = `${date}T${time}:00Z`;
              timePatch = { startAt, endAt: new Date(Date.parse(startAt) + span * 3_600_000).toISOString() };
            }
            await calendarApi.updateCalendarEvent(id, {
              ...(patch.status !== undefined && { status: patch.status }),
              ...(patch.title !== undefined && { title: patch.title }),
              ...(patch.location !== undefined && { location: patch.location }),
              ...(timePatch ?? {}),
            });
          }
          await get().fetchEvents();
        } catch (err) {
          console.error('Failed to update event:', err);
          // Rollback by refetching server state
          await get().fetchEvents();
          throw err;
        }
      },

      deleteEvent: async (id) => {
        // Snapshot for rollback
        const deleted = get().events.find((ev) => ev.id === id);
        // Optimistic remove
        set((s) => ({ events: s.events.filter((ev) => ev.id !== id) }));
        try {
          await calendarApi.deleteCalendarEvent(id);
          await get().fetchEvents();
        } catch (err: unknown) {
          const status = (err as import('axios').AxiosError)?.response?.status;
          if (status === 404) {
            // Event didn't exist in DB — local removal is correct, no rollback needed
            return;
          }
          // Real failure — restore the event and show error
          if (deleted) {
            set((s) => ({ events: [...s.events, deleted] }));
          }
          console.error('Delete calendar event failed:', err);
          import('sonner').then(({ toast }) => {
            toast.error('Failed to remove calendar event — please try again');
          });
        }
      },

      setAvailability: (date, status) =>
        set((s) => ({ availability: { ...s.availability, [date]: status } })),

      clearAvailability: (date) =>
        set((s) => {
          const next = { ...s.availability };
          delete next[date];
          return { availability: next };
        }),

      getStatus: (date) => {
        const state = get();
        // Manual override takes priority
        if (state.availability[date]) return state.availability[date];
        // Auto-derive from events — cancelled events (vacated hearings) don't count as busy
        const dayEvents = state.events.filter((e) => e.date === date && e.status !== 'cancelled');
        if (dayEvents.length === 0) return null;
        const totalHours = dayEvents.reduce((s, e) => s + e.span, 0);
        if (totalHours >= 6) return 'busy';
        if (totalHours >= 1) return 'partial';
        return null;
      },

      getEventsForDate: (date) => {
        return get().events.filter((e) => e.date === date);
      },

      getEventsForDateByBarrister: (date) => {
        const events = get().events.filter((e) => e.date === date);
        const grouped: Record<string, CalEvent[]> = {};
        for (const e of events) {
          const key = e.barrister ?? 'Unassigned';
          if (!grouped[key]) grouped[key] = [];
          grouped[key].push(e);
        }
        return grouped;
      },

      getBarristerEvents: (barrister, date) => {
        return get().events.filter((e) => e.date === date && e.barrister === barrister);
      },
    }),
    {
      name: 'jurivoxa-diary',
      version: 4,
      partialize: () => ({}),
      migrate: () => {
        // v4: nothing persisted — events and availability always loaded from API.
        return { events: [], availability: {} };
      },
    },
  ),
);
